import type { ProtoProfile, VaultProfile } from '@ducat-unit/core';
import {
  derive_ratio,
  get_vault_terms,
  is_vault_active,
  select_base_price_commit,
  verify_proto_profile,
  verify_vault_profile,
} from '@ducat-unit/core/lib';

// Current liquidation data belongs to the vault's retained signed contracts.
// Historical vaults must be paired with their own protocol snapshot.
export function retained_liquidation_price(vault: VaultProfile | undefined, proto: ProtoProfile | undefined): number {
  if (!vault || !proto) return 0;
  try {
    verify_proto_profile(proto);
    verify_vault_profile(vault, proto);
    if (!is_vault_active(vault) || vault.unit_balance === 0) return 0;
    return select_base_price_commit(vault.price_commits)?.thold_price ?? 0;
  } catch {
    return 0; // Existing display convention: unavailable / no liquidation price.
  }
}

const SATS = 100_000_000n;
const ceil_div = (n: bigint, d: bigint) => (n + d - 1n) / d;

// Display estimates only. Action construction still resolves exact signed oracle
// buckets. Protocol prices are whole USD; floor the live market observation for
// conservative, reproducible previews using Core's integer ratio arithmetic.
export function vault_preview(proto: ProtoProfile | undefined, net_sats: number, debt: number, market_price: number) {
  const unavailable = {
    ready: false,
    ratio: 0,
    liquidation_price: 0,
    capacity: 0,
    locked_sats: Number.isSafeInteger(net_sats) ? Math.max(0, net_sats) : 0,
    divisibility: 2,
  };
  if (!proto || !Number.isSafeInteger(net_sats) || net_sats < 0 || !Number.isFinite(debt) || debt < 0) return unavailable;
  try {
    verify_proto_profile(proto);
    const terms = get_vault_terms(proto.proto_terms);
    const asset = proto.proto_assets.find((entry) => entry.id === terms.unit_asset_id);
    if (!asset || !Number.isInteger(asset.div) || asset.div < 0 || asset.div > 38) return unavailable;
    const scale = 10 ** asset.div;
    if (!Number.isSafeInteger(scale)) return unavailable;
    const debt_units = Math.round(debt * scale);
    if (!Number.isSafeInteger(debt_units) || Math.abs(debt * scale - debt_units) > 1e-6) return unavailable;
    const floor = Math.round(terms.vault_ratio_min * 100);
    const threshold = Math.round(terms.liquidation_thold * 100);
    if (
      !Number.isSafeInteger(floor) ||
      floor <= 0 ||
      floor / 100 !== terms.vault_ratio_min ||
      !Number.isSafeInteger(threshold) ||
      threshold <= 0 ||
      threshold / 100 !== terms.liquidation_thold
    )
      return unavailable;
    const price = Math.floor(market_price);
    const has_price = Number.isSafeInteger(price) && price > 0 && price <= 0xffff_ffff;
    const result = derive_ratio({
      net_sats: BigInt(net_sats),
      debt_base_units: BigInt(debt_units),
      divisibility: asset.div,
      price: has_price ? BigInt(price) : null,
    });
    const ratio = result.kind === 'ratio' ? result.ratio : result.kind === 'debt_free' ? Infinity : 0;
    const liquidation_price =
      debt_units === 0
        ? 0
        : net_sats === 0
          ? Infinity
          : Number(ceil_div(BigInt(debt_units) * BigInt(threshold) * SATS, BigInt(net_sats) * BigInt(scale) * 100n));
    const capacity = has_price ? Number((BigInt(net_sats) * BigInt(price) * BigInt(scale) * 100n) / (SATS * BigInt(floor))) / scale : 0;
    const locked_sats =
      debt_units === 0
        ? 0
        : has_price
          ? Math.min(net_sats, Number(ceil_div(BigInt(debt_units) * SATS * BigInt(floor), BigInt(price) * BigInt(scale) * 100n)))
          : net_sats;
    return { ready: has_price, ratio, liquidation_price, capacity, locked_sats, divisibility: asset.div };
  } catch {
    return unavailable;
  }
}

// Opening deposits include the locked vault minimum. SDK open quotes fund fees
// and UNIT postage from separate wallet coins, not by reducing this collateral.
export function create_vault_preview(proto: ProtoProfile | undefined, deposit_sats: number, debt: number, market_price: number) {
  const unavailable = { ...vault_preview(undefined, 0, debt, market_price), net_sats: 0, health: null, amount_valid: false };
  if (!proto || !Number.isSafeInteger(deposit_sats) || deposit_sats < 0) return unavailable;
  try {
    const terms = get_vault_terms(proto.proto_terms);
    if (!Number.isSafeInteger(terms.vault_value_min) || terms.vault_value_min < 0 || deposit_sats < terms.vault_value_min)
      return unavailable;
    const net_sats = deposit_sats - terms.vault_value_min;
    const preview = vault_preview(proto, net_sats, debt, market_price);
    const minimum = terms.unit_balance_min / 10 ** preview.divisibility;
    return {
      ...preview,
      net_sats,
      health: preview.ready && debt > 0 ? Math.round(preview.ratio * 100) : null,
      amount_valid: preview.ready && debt > 0 && debt >= minimum && debt <= preview.capacity,
    };
  } catch {
    return unavailable;
  }
}
